import MenuIcon from "@mui/icons-material/Menu";
import { AppBar, Box, Button, Container, Divider, Drawer, IconButton, List, ListItem, ListItemIcon, ListItemText, Toolbar, Typography, useTheme } from "@mui/material";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { UserContext } from "../../contexts/UserContext";
import { drawerListItem, headerContainer, headerLink } from "../../styles/headerStyles";
import LanguageDropdown from "../Common/LanguageDropdown";
import UserDropdown from "../Login/UserDropdown";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";

const Header = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { user } = useContext(UserContext);

  const handleDrawerOpen = useCallback(() => setDrawerOpen(true), []);
  const handleDrawerClose = useCallback(() => setDrawerOpen(false), []);

  const menuItems = useMemo(
    () => [
      { text: t("recipes"), path: "/recipes", icon: <RestaurantIcon color="primary" /> },
      ...(user
        ? [
            { text: t("menuPlanTitle"), path: "/menu-plan", icon: <CalendarMonthIcon color="primary" /> },
            { text: t("frequentItemsList"), path: "/frequent", icon: <FormatListBulletedIcon color="primary" /> },
            { text: t("shoppingListTitle"), path: "/shopping-list", icon: <ShoppingCartIcon color="primary" /> },
          ]
        : []),
    ],
    [t, user],
  );

  const languages = useMemo(
    () => [
      { code: "ee", label: "Eesti", icon: "/assets/flags/ee_flag.ico" },
      { code: "en", label: "English", icon: "/assets/flags/en_flag.ico" },
      { code: "ru", label: "Русский", icon: "/assets/flags/ru_flag.png" },
    ],
    [],
  );

  return (
    <AppBar position="static" sx={headerContainer}>
      <Container disableGutters={true}>
        <Toolbar>
          <Typography
            variant="logo"
            component={Link}
            to="/"
            sx={(theme) => ({
              flexGrow: 1,
              whiteSpace: "nowrap",
              textDecoration: "none",
              color: theme.typography.logo.color,
            })}
          >
            {t("appTitle")}
          </Typography>

          {/* Desktop Menu */}
          <div style={{ display: "flex", alignItems: "center" }}>
            {menuItems.map((item, index) => (
              <Button
                startIcon={item.icon}
                key={index}
                color="inherit"
                component={Link}
                to={item.path}
                sx={(theme) => ({ ...headerLink(theme), textTransform: "none" })}
              >
                {item.text}
              </Button>
            ))}

            {/* Conditionally render LanguageDropdown if Mobile Menu Icon is not visible */}

            <Box sx={{ display: { xs: "none", md: "flex" } }}>
              <LanguageDropdown languages={languages} />
            </Box>
            {/* Mobile Menu Icon */}
            <IconButton color="inherit" edge="start" onClick={handleDrawerOpen} sx={{ display: { xs: "flex", md: "none" } }}>
              <MenuIcon />
            </IconButton>

            <UserDropdown />
          </div>

          {/* Drawer for Mobile */}
          <Drawer anchor="right" open={drawerOpen} onClose={handleDrawerClose}>
            <List>
              {menuItems.map((item, index) => (
                <ListItem button key={index} component={Link} to={item.path} onClick={handleDrawerClose} sx={drawerListItem(theme)}>
                  <ListItemText
                    primary={
                      <Typography variant="body1" sx={{ fontSize: theme.typography.body1.fontSize, fontFamily: theme.typography.fontFamily }}>
                        {item.text}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}

              <Divider />

              {languages.map((lang) => (
                <ListItem
                  button
                  key={lang.code}
                  onClick={() => {
                    i18n.changeLanguage(lang.code);
                    localStorage.setItem("lang", lang.code);
                    handleDrawerClose();
                  }}
                >
                  <ListItemIcon>
                    <Box component="img" src={lang.icon} alt={lang.label} sx={{ width: 20 }} />
                  </ListItemIcon>
                  <ListItemText primary={lang.label} />
                </ListItem>
              ))}
            </List>
          </Drawer>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Header;
